/**
 * Hook for managing module detailed answers.
 * @module useModuleAnswers
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { getModuleAnswers } from '@/lib/simulationResultsApi';
import { mapApiModuleAnswerToDomain } from '@/lib/mappers';
import { ModuleAnswer, ModuleResult } from '@/types/simulation-results';
import { shouldPollModuleAnalysis } from '@/lib/analysisStatus';

const POLL_INTERVAL_MS = 10_000;

interface UseModuleAnswersOptions {
  readonly sessionModules?: readonly ModuleResult[];
  readonly activeModule?: string;
  readonly sessionId?: string;
}

interface UseModuleAnswersReturn {
    readonly answers: ModuleAnswer[];
    readonly isLoading: boolean;
    readonly error: string | null;
    readonly fetchAnswers: (sessionId: string, module: string) => Promise<void>;
    readonly clearAnswers: () => void;
}

export function useModuleAnswers(options: UseModuleAnswersOptions = {}): UseModuleAnswersReturn {
    const { sessionModules = [], activeModule, sessionId } = options;
    const [answers, setAnswers] = useState<ModuleAnswer[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fetchAnswersRef = useRef<(sessionId: string, module: string, isSilent?: boolean) => Promise<void>>(
        async () => {},
    );

    const fetchAnswers = useCallback(async (sid: string, module: string, isSilent = false) => {
        if (!isSilent) {
            setAnswers([]);
            setIsLoading(true);
        }
        setError(null);
        try {
            const rawAnswers = await getModuleAnswers(sid, module);
            const domainAnswers = rawAnswers.map(mapApiModuleAnswerToDomain);
            setAnswers(domainAnswers);
        } catch (err) {
            console.error('Failed to fetch module answers:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            if (!isSilent) {
                setIsLoading(false);
            }
        }
    }, []);

    fetchAnswersRef.current = fetchAnswers;

    const clearAnswers = useCallback(() => {
        setAnswers([]);
        setError(null);
        setIsLoading(false);
    }, []);

    const activeModuleResult = sessionModules.find((mod) => mod.module === activeModule);
    const shouldPollAnswers = activeModuleResult != null
        && sessionId != null
        && activeModule != null
        && shouldPollModuleAnalysis(activeModuleResult);

    useEffect(() => {
        if (!shouldPollAnswers || isLoading || !sessionId || !activeModule) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            void fetchAnswersRef.current(sessionId, activeModule, true);
        }, POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [shouldPollAnswers, isLoading, sessionId, activeModule]);

    return {
        answers,
        isLoading,
        error,
        fetchAnswers,
        clearAnswers,
    };
}
